import { PAGE_TYPES } from '../../components/tracking/gtm/consts';
import { addToDataLayer } from '../../components/tracking/gtm/utils';
import { TrackingEventName } from './types';

import type { Product } from '@virginexperiencedays/products';
import type { ProductPromo } from '@virginexperiencedays/promos';

/**
 * PDP interactions
 *
 * Media Grid
 * - Swipe to next/previous
 * - Click next/previous
 * - Click on image
 * - Click on show all photos
 * - Click on play/pause video
 *
 * PurchasePanel, Media Grid
 * - Click on Add to Basket
 * - Click on Klarna
 * - ..add more payment provider..
 *
 * USP Grid
 * - ClickScrollToLocations
 *
 * About, AdditionalDetails, Reviews
 * - Click on read more/less
 *
 * AdditionalDetails
 * - Click on accordion expand/collapse
 *
 * ReviewSummary, CustomerReviews
 * - Click on All Reviews
 *
 * Category Pills
 * - Click on category
 *
 * Location
 * - Click on Location Region
 * - Click on Map
 *
 *
 * =============
 * SAMPLE USAGE:
 * =============
 *
 * import { trackPdp, trackPdpMediaGrid, trackPdpReview, formatTrackingData } from '.../web/libs/tracking/pdp';
 * import { PAGE_TYPES } from '.../web/components/tracking/gtm/consts';
 *
 * const trackingData = formatTrackingData({ product, trackingPageType, productPromo });
 * trackPdp({
 *   ...trackingData,
 *   interaction: PdpInteraction.ClickShowAllPhotos,
 *   module: PdpModules.MediaGrid,
 * })
 *
 * trackPdp({
 *   interaction: PdpInteraction.ClickShowAllPhotos,
 *   pageType: PAGE_TYPES.product,
 *   title: 'Aston Martin Thrill',
 *   module: PdpModules.MediaGrid,
 *   moduleTitle: null,
 *   sku: 'PAMTHR',
 *   promo: 'Virgin Loves',
 *   averageRating: 4,
 *   displayPrice: 86.25,
 *   rrp: 115,
 *   percentOff: 25,
 *   location: formatLocationProp(['Birmingham, West Midlands', 'Seighford, Staffordshire', '...']),
 * })
 *
 * trackPdpMediaGrid({
 *   pageType: PAGE_TYPES.product,
 *   title: 'Aston Martin Thrill',
 *   moduleTitle: null,
 *   sku: 'PAMTHR',
 *   promo: 'Virgin Loves',
 *   averageRating: 4,
 *   displayPrice: 86.25,
 *   rrp: 115,
 *   percentOff: 25,
 *   location: 'Birmingham, West Midlands|Seighford, Staffordshire',
 *
 *   // MediaGridTrackingProps
 *   currentIndex: 2,
 *   total: 4,
 *   action: 'next',
 *   gesture: 'swipe',
 * })
 *
 * trackPdpReview({
 *   pageType: PAGE_TYPES.product,
 *   title: 'Aston Martin Thrill',
 *   moduleTitle: 'One of the best adventure experience!',
 *   sku: 'PAMTHR',
 *   promo: 'Virgin Loves',
 *   averageRating: 4,
 *   displayPrice: 86.25,
 *   rrp: 115,
 *   percentOff: 25,
 *   location: 'Birmingham, West Midlands|Seighford, Staffordshire',
 *
 *   // ReviewTrackingProps
 *   currentIndex: 2,
 *   total: 37,
 *   action: 'expand',
 *   type: 'all',
 *   rating: 3.5,
 *   dateReviewed: '21 Sep 2023',
 * })
 */
export function trackPdp({
  pageType,
  title,
  module,
  moduleTitle,
  sku,
  promo,
  averageRating,
  displayPrice,
  rrp,
  percentOff,
  location,
  imageInteracted,
  reviewInteracted,
  interaction,
  pdpType,
  pdpCollectionChoiceInteracted,
}: PdpInteractionProps) {
  const data: PdpInteractionData = {
    event: TrackingEventName.pdpInteraction,
    pdp_page_type: pageType,
    pdp_title: title,
    pdp_module: module,
    pdp_module_title: moduleTitle,
    pdp_sku: sku,
    pdp_promo: promo,
    pdp_average_rating: averageRating,
    pdp_display_price: displayPrice,
    pdp_rrp: rrp,
    pdp_percent_off: percentOff,
    pdp_location: location,
    pdp_image_interacted: imageInteracted,
    pdp_review_interacted: reviewInteracted,
    pdp_interaction: interaction,
    pdp_type: pdpType,
    pdp_collection_choice_interacted: pdpCollectionChoiceInteracted,
  };

  addToDataLayer(data);
}

export const trackPdpMediaGrid = ({
  currentIndex,
  total,
  action,
  gesture,
  module,
  ...rest
}: Omit<PdpInteractionProps, 'module' | 'imageInteracted' | 'interaction'> & {
  currentIndex: number;
  total: number;
  action: 'next' | 'prev' | 'image';
  gesture: 'swipe' | 'click';
  module?: PdpInteractionProps['module'];
}) => {
  const interaction = {
    swipe: {
      prev: PdpInteraction.SwipePrev,
      next: PdpInteraction.SwipeNext,
    },
    click: {
      prev: PdpInteraction.ClickPrev,
      next: PdpInteraction.ClickNext,
      image: PdpInteraction.ClickImage,
    },
  };

  trackPdp({
    ...rest,
    module: module || PdpModules.MediaGrid,
    imageInteracted: `${currentIndex + 1} of ${total}`,
    interaction: interaction?.[gesture]?.[action],
  });
};

export const trackPdpReview = ({
  currentIndex,
  total,
  action,
  source,
  type,
  rating,
  dateReviewed,
  module,
  moduleTitle,
  ...rest
}: Omit<PdpInteractionProps, 'module' | 'reviewInteracted' | 'interaction'> & {
  currentIndex: number;
  total: number;
  action: 'expand' | 'collapse';
  type: 'featured' | 'all';
  rating: number;
  dateReviewed: string;
  source?: 'modal' | 'drawer';
  module?: PdpInteractionProps['module'];
}) => {
  const interaction = {
    expand: PdpInteraction.ClickReadMore,
    collapse: PdpInteraction.ClickReadLess,
  };
  const types = {
    featured: PdpModules.FeaturedReviews,
    all: PdpModules.CustomerReviews,
  };

  trackPdp({
    ...rest,
    module: types?.[type] || module,
    moduleTitle: source ? `(${source}) ${moduleTitle}` : moduleTitle,
    reviewInteracted: [
      `${currentIndex + 1} of ${total}`,
      `${rating} star${rating > 1 && 's'}`,
      dateReviewed,
    ].join('|'),
    interaction: interaction?.[action],
  });
};

export const trackPdpPayment = ({
  paymentMethod,
  action,
  ...rest
}: Omit<PdpInteractionProps, 'interaction'> & {
  paymentMethod: string;
  action: 'click' | 'load' | 'success' | 'error';
}) => {
  const interaction = {
    ['google-pay']: {
      click: PdpInteraction.ClickGooglePay,
      load: PdpInteraction.PaymentGooglePayLoaded,
      success: PdpInteraction.PaymentGooglePaySucceeded,
      error: PdpInteraction.PaymentGooglePayFailed,
    },
    ['apple-pay']: {
      click: PdpInteraction.ClickApplePay,
      load: PdpInteraction.PaymentApplePayLoaded,
      success: PdpInteraction.PaymentApplePaySucceeded,
      error: PdpInteraction.PaymentApplePayFailed,
    },
    default: {
      click: PdpInteraction.ClickStripePayment,
      load: PdpInteraction.StripePaymentLoaded,
      success: PdpInteraction.StripePaymentSuccess,
      error: PdpInteraction.StripePaymentFailed,
    },
  };

  const fallback = [interaction.default?.[action], paymentMethod]
    .filter((val) => !!val)
    .join(' - ');

  trackPdp({
    ...rest,
    interaction: interaction?.[paymentMethod]?.[action] || fallback,
  });
};

export const trackPdpCollectionChoice = ({
  collectionChoiceSlug,
  collectionChoiceTitle,
  ...rest
}: Omit<PdpInteractionProps, 'pdpCollectionChoiceInteracted'> & {
  collectionChoiceSlug: string;
  collectionChoiceTitle: PdpInteractionProps['title'];
}) => {
  trackPdp({
    ...rest,
    pdpType: PdpTypes.CollectionProduct,
    pdpCollectionChoiceInteracted: `${collectionChoiceSlug}|${collectionChoiceTitle}`,
  });
};

type FormatTrackingDataProps = {
  product: Product;
  trackingPageType?: string;
  productPromo?: ProductPromo;
};

export const formatTrackingData = ({
  product,
  productPromo,
  trackingPageType,
}: FormatTrackingDataProps): PdpInteractionProps => {
  const location = product?.locations?.map((location) => location.name);

  const pdpType = product?.isCollectionProduct ? PdpTypes.CollectionProduct : PdpTypes.Product;

  const sku = product?.promocode ? `${product?.sku} ${product?.promocode}` : product?.sku;
  const title = product?.title;

  return {
    pageType: trackingPageType || PAGE_TYPES.Product,
    title,
    module: null,
    moduleTitle: null,
    sku,
    promo: productPromo?.config?.text,
    averageRating: product?.reviews?.averageRating,
    displayPrice: product?.price?.displayPrice,
    rrp: product?.price?.rrp,
    percentOff: product?.price?.percentOff,
    location: formatLocationProp(location),
    imageInteracted: null,
    reviewInteracted: null,
    interaction: null,
    pdpType,
    pdpCollectionChoiceInteracted: null,
  };
};

export const formatLocationProp = (locations?: string[]): string =>
  Array.isArray(locations) && locations?.length > 0 ? locations.join('|') : null;

export type PdpInteractionProps = {
  pageType: string;
  title: string;
  module: PdpModules;
  moduleTitle?: string;
  sku: string;
  promo?: string;
  averageRating: number;
  displayPrice?: number;
  rrp: number;
  percentOff?: number;
  location?: string;
  imageInteracted?: string;
  reviewInteracted?: string;
  interaction: PdpInteraction;
  pdpType: PdpTypes;
  pdpCollectionChoiceInteracted?: string;
};

export type PdpInteractionData = {
  event: TrackingEventName;
  pdp_page_type: string;
  pdp_title: string;
  pdp_module: PdpModules;
  pdp_module_title: string;
  // full sku (sku + promocode)
  pdp_sku: string;
  pdp_promo: string;
  pdp_average_rating: number;
  pdp_display_price: number;
  pdp_rrp: number;
  pdp_percent_off: number;
  // Pipe-separated location names
  pdp_location: string;
  // {imagePositionInteracted} of {totalImages}
  pdp_image_interacted: string;
  // {reviewPositionInteracted} of {totalreviews} | {individualReviewRating} star/s | {dateReviewed DD MMM YYYY}
  pdp_review_interacted: string;
  pdp_interaction: PdpInteraction;
  pdp_type: PdpTypes;
  pdp_collection_choice_interacted: string;
};

export enum PdpTypes {
  Product = 'Product',
  CollectionProduct = 'Collection Product',
}

export enum PdpModules {
  ReviewSummary = 'review_summary',
  MediaGrid = 'media_grid',
  MediaLightbox = 'media_lightbox',
  About = 'about',
  UspGrid = 'usp_grid',
  FeaturedReviews = 'featured_reviews',
  PurchasePanel = 'purchase_panel',
  AdditionalDetails = 'additional_details',
  Locations = 'locations',
  CustomerReviews = 'customer_reviews',
  CategoryPills = 'category_pills',
  // top section
  ProductRecommendationsGs = 'recommendations_gs',
  // bottom section
  ProductRecommendations = 'recommendations',
  CollectionChoicesWidget = 'collection_choices_widget',
  CollectionChoiceOverlay = 'collection_choice_overlay',
}

export enum PdpInteraction {
  // Media Grid
  SwipeNext = 'Swipe to next',
  SwipePrev = 'Swipe to previous',
  ClickNext = 'Click next',
  ClickPrev = 'Click previous',
  ClickImage = 'Click on image', // opens Lightbox
  ClickShowAllPhotos = 'Click on show all photos',
  ClickPlayVideo = 'Click on play video', // from Lightbox
  ClickPauseVideo = 'Click on pause video', // from Lightbox

  // PurchasePanel, Media Grid
  ClickAddToBasket = 'Click on Add to Basket', // from Lightbox or Purchase Panel
  ClickKlarna = 'Click on Klarna',

  // Purchase Panel
  ClickGooglePay = 'Click on GooglePay',
  PaymentGooglePayLoaded = 'Payment GooglePay Loaded',
  PaymentGooglePaySucceeded = 'Payment GooglePay Succeeded',
  PaymentGooglePayFailed = 'Payment GooglePay Failed',
  ClickApplePay = 'Click on ApplePay',
  PaymentApplePayLoaded = 'Payment ApplePay Loaded',
  PaymentApplePaySucceeded = 'Payment ApplePay Succeeded',
  PaymentApplePayFailed = 'Payment ApplePay Failed',
  // generic payment provider as fallback
  ClickStripePayment = 'Click on Stripe Payment',
  StripePaymentLoaded = 'Stripe Payment Loaded',
  StripePaymentSuccess = 'Stripe Payment Succeeded',
  StripePaymentFailed = 'Stripe Payment Failed',

  // USP Grid
  ClickScrollToLocations = 'Click on scroll to locations', // from USP Grid

  // About, AdditionalDetails, Reviews
  ClickReadMore = 'Click on read more',
  ClickReadLess = 'Click on read less',

  // AdditionalDetails
  ClickAccordionExpand = 'Click on accordion expand',
  ClickAccordionCollapse = 'Click on accordion collapse',

  // CollectionChoicesWidget
  ClickCollectionChoice = 'Click on Collection Choice',

  // ReviewSummary, CustomerReviews
  ClickAllReviews = 'Click on All Reviews', // opens modal/drawer

  // Category Pills
  ClickCategory = 'Click on category',

  // Location
  ClickMap = 'Click on Map',
  ClickLocationSelect = 'Click on Location Select', // opens drawer
  SelectLocation = 'Select Location', // selects a location from region list sidebar or drawer

  // CollectionChoiceOverlay
  ClickCloseOverlay = 'Click on Close Overlay',
  ClickBackToCollection = 'Click on Back to Collection',
}
